import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import styles from "./DatePickerField.module.css";
import { useField } from "formik";
import { useCallback, useMemo, useState } from "react";
import IMAGES from "../../assets/images/images";
import Asterisk from "../asterisk/Asterisk";
import { FormFieldLabel as Label, FormErrorLabel as ErrorLabel } from "../label/Labels";
import { formatDateInDMY, formatDateToDDMonYYYY, formatISOToDDMonYYYY } from "../../utils/arrayUtils";
import { dateToUtcUnixSeconds, utcUnixSecondsToDate } from "../../utils/dateTime";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const toISO = (d) => {
  if (!d) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const stripTime = (d) => {
  if (!d) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};
// this component take date as utc unix second and return the same formatted date
const DatePickerField = ({
  name,
  minDate,
  maxDate,
  disabled = false,
  onChange,
  className,
  labelstyle,
  ...props
}) => {
  const [field, meta, helpers] = useField(name);
  const [viewMode, setViewMode] = useState("day");
  const hasError = meta.touched && !!meta.error;
  const errorMessage = meta.error;
  const strippedMinDate = useMemo(() => stripTime(minDate), [minDate]);
  const strippedMaxDate = useMemo(() => stripTime(maxDate), [maxDate]);

  const isDateDisabled = useCallback(
    (date) => {
      const d = stripTime(date);
      if (!d) return false;
      if (strippedMinDate && d < strippedMinDate) return true;
      if (strippedMaxDate && d > strippedMaxDate) return true;
      return false;
    },
    [strippedMinDate, strippedMaxDate]
  );
  const formatDateDDMMYYYY = (value) => {
    if (!value) return "---";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "---";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();

    return `${dd}/${mm}/${yyyy}`;
  };

  const getInputClassName = () => {
    let inputClassName = styles.datepickerInput;
    if (hasError) inputClassName += ` ${styles.datepickerInputError}`;
    if (className) inputClassName += ` ${className}`;
    return inputClassName;
  };

  const getDayClassName = useCallback(
    (date, selectedDate) => {
      if (isDateDisabled(date)) return styles.dayDisabled;
      const isSelected =
        selectedDate && date.toDateString() === selectedDate.toDateString();
      if (isSelected) return styles.daySelected;
      const isToday = date.toDateString() === new Date().toDateString();
      if (isToday) return styles.dayToday;
      return styles.dayDefault;
    },
    [isDateDisabled]
  );

  const renderCustomHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    decreaseYear,
    increaseYear,
    changeMonth,
    changeYear,
  }) => {
    const currentYear = date.getFullYear();

    if (viewMode === "year") {
      const startYear = currentYear - 4;
      const years = Array.from({ length: 12 }, (_, i) => startYear + i);
      return (
        <div className={styles.customHeader}>
          <div className={styles.customHeaderNavRow}>
            <button type="button" className={styles.customHeaderNav} onClick={() => changeYear(currentYear - 12)}>‹</button>
            <div className={styles.customHeaderLabel}>{startYear} – {startYear + 11}</div>
            <button type="button" className={styles.customHeaderNav} onClick={() => changeYear(currentYear + 12)}>›</button>
          </div>
          <div className={styles.customHeaderGrid}>
            {years.map((yr) => (
              <button
                key={yr}
                type="button"
                className={`${styles.customHeaderGridItem} ${yr === currentYear ? styles.customHeaderGridItemActive : ""}`}
                onClick={() => { changeYear(yr); setViewMode("month"); }}
              >
                {yr}
              </button>
            ))}
          </div>
        </div>
      );
    }

    if (viewMode === "month") {
      return (
        <div className={styles.customHeader}>
          <div className={styles.customHeaderNavRow}>
            <button type="button" className={styles.customHeaderNav} onClick={decreaseYear}>‹</button>
            <div className={styles.customHeaderLabel}>
              <span className={styles.customHeaderClickable} onClick={() => setViewMode("year")}>{currentYear}</span>
            </div>
            <button type="button" className={styles.customHeaderNav} onClick={increaseYear}>›</button>
          </div>
          <div className={styles.customHeaderGrid}>
            {MONTHS.map((m, i) => (
              <button
                key={m}
                type="button"
                className={`${styles.customHeaderGridItem} ${i === date.getMonth() ? styles.customHeaderGridItemActive : ""}`}
                onClick={() => { changeMonth(i); setViewMode("day"); }}
              >
                {m}
              </button>
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className={`${styles.customHeader} ${styles.customHeaderDay}`}>
        <button type="button" className={styles.customHeaderNav} onClick={decreaseMonth}>‹</button>
        <div className={styles.customHeaderLabel}>
          <span className={styles.customHeaderClickable} onClick={() => setViewMode("month")}>{MONTHS[date.getMonth()]}</span>{" "}
          <span className={styles.customHeaderClickable} onClick={() => setViewMode("year")}>{currentYear}</span>
        </div>
        <button type="button" className={styles.customHeaderNav} onClick={increaseMonth}>›</button>
      </div>
    );
  };

  //const selectedDate = field.value ? new Date(field.value) : null;
  const selectedDate = field.value ? utcUnixSecondsToDate (field.value): null;

  const handleChange = (d) => {
    //const iso = toISO(d);
    const unix = dateToUtcUnixSeconds(d);
    helpers.setTouched(true, false);
    helpers.setValue(unix, true);
    onChange?.(unix);
  };

  const handleBlur = () => {
    helpers.setTouched(true);
  };

  return (
    <div className={styles.datepickerWrapper}>
      <div className={styles.label}>
        {props.label && <Label text={props.label} style={labelstyle} />}
        {props.isMandatory ? <Asterisk /> : null}
      </div>
      {!props.isPreviewMode ? (<>
        <div className={styles.datepickerContainer}>
          <DatePicker
            selected={selectedDate}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={disabled}
            minDate={minDate}
            maxDate={maxDate}
            dateFormat={props.dateFormat}
            placeholderText={props.placeholderText}
            className={getInputClassName()}
            calendarClassName={`${styles.customCalendar} ${viewMode !== "day" ? styles.customCalendarPickerMode : ""}`}
            dayClassName={(date) => getDayClassName(date, selectedDate)}
            renderCustomHeader={renderCustomHeader}
            onCalendarClose={() => setViewMode("day")}
            onKeyDown={(e) => e.preventDefault()}
            onChangeRaw={(e) => e.preventDefault()}
            isClearable
          />
          <div className={styles.datepickerIcon}>
            <img className={styles.dateicon} src={IMAGES.calender} alt="Calendar" />
          </div>
        </div>
        {!disabled && hasError && (
          <label className={styles.datepickerError}>{errorMessage}</label>
        )}
      </>) : (
        <div className={styles.previewText}>
          {formatDateInDMY(field?.value) || "---"}
        </div>

      )}
    </div>
  );
};

export default DatePickerField;